import type {
  Absence,
  BankHoliday,
  Booking,
  BookingType,
  Resource,
  WorkingHoursProfile,
} from '@/types';
import { calculatePeriodCapacity } from '@/utils/heatmap-utils';

export interface ResourceAllocationCapacity {
  /** Booked hours for the resource inside the period */
  bookedHours: number;
  /**
   * Working-profile hours for the period, minus the hours removed by bank
   * holidays and absences.
   */
  capacityHours: number;
  /** bookedHours / capacityHours × 100 */
  allocationPct: number;
}

export const EMPTY_ALLOCATION_CAPACITY: ResourceAllocationCapacity = {
  bookedHours: 0,
  capacityHours: 0,
  allocationPct: 0,
};

/**
 * Allocation of a single resource over a period, using the resource's own
 * working hours profile when it is configured and the global profile
 * otherwise.  Bank holidays and absences reduce the capacity exactly as they
 * do for the planner's utilisation badge.
 */
export function calculateResourceAllocation(
  resource: Resource,
  periodStart: Date,
  periodEnd: Date,
  bookings: Booking[],
  globalWorkingProfile: WorkingHoursProfile,
  bankHolidays?: BankHoliday[],
  absences?: Absence[],
  bookingTypes?: BookingType[],
): ResourceAllocationCapacity {
  const { totalBookedHours, totalCapacityHours, utilizationPercentage } = calculatePeriodCapacity(
    resource.id,
    periodStart,
    periodEnd,
    bookings,
    resource,
    globalWorkingProfile,
    bankHolidays,
    absences,
    bookingTypes,
  );

  return {
    bookedHours: totalBookedHours,
    capacityHours: totalCapacityHours,
    allocationPct: utilizationPercentage,
  };
}

/**
 * Average allocation across a set of resources:
 *
 *   Σ booked hours / Σ capacity hours × 100
 *
 * Weighting by hours rather than averaging each resource's percentage means a
 * part-time resource cannot skew the figure of a whole team.
 */
export function averageAllocationPct(
  entries: Pick<ResourceAllocationCapacity, 'bookedHours' | 'capacityHours'>[],
): number {
  const bookedHours = entries.reduce((sum, e) => sum + e.bookedHours, 0);
  const capacityHours = entries.reduce((sum, e) => sum + e.capacityHours, 0);
  if (capacityHours <= 0) return 0;
  return Math.min(999, Math.round((bookedHours / capacityHours) * 100));
}
